import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Building2, MapPin, ShieldCheck, Clock, Award, 
  FlaskConical, Package, ChevronRight, Search, 
  ArrowLeft, Check, ShoppingBag, X, Mic, CheckCircle2,
  Share2, Heart, Info, Sparkles, Tag, Flame, Plus, Minus,
  Layers, ArrowRight, Trash2
} from "lucide-react";
import axios from "axios";
import { useLocationContext } from "../../context/LocationContext";
import { fetchLabDetails, fetchLabDistance } from "../../services/LabTestApi/LabDetails";
import { getAllTests } from "../../services/LabTestApi/PopularTest";
import { useCartActions } from "../../hooks/useCartActions";
import TestCard from "./TestCard";
import PackageCard from "./PackageCard";

export default function LabDetails() {
  const { id } = useParams();
  const labId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const stateLab = location.state?.lab;
  const { coords, address, selectedCity } = useLocationContext();

  const cart = useSelector((state) => state.cart.items || []);
  const { addWithReplaceCheck, removeItem, LoginPopupComponent } = useCartActions();

  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distance, setDistance] = useState(null);
  const [activeTab, setActiveTab] = useState("tests"); // 'tests' | 'packages'
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  useEffect(() => {
    const loadLabAndData = async () => {
      setLoading(true);
      try {
        // 1. Lab Info
        const labData = await fetchLabDetails(labId);
        setLab(labData || stateLab);

        // 2. Distance
        const sourceLat = selectedCity?.latitude ?? selectedCity?.lat ?? coords?.lat;
        const sourceLng = selectedCity?.longitude ?? selectedCity?.lng ?? coords?.lng;

        if (sourceLat && sourceLng) {
          const distData = await fetchLabDistance({
            labId,
            userLat: Number(sourceLat),
            userLng: Number(sourceLng),
          });
          if (distData && distData.length > 0) {
            const val = distData[0]?.labValue || distData[0]?.Distance;
            const num = parseFloat(val);
            setDistance(isNaN(num) ? null : num);
          }
        }

        // 3. Lab Tests - Load ALL tests specifically for this lab (or fallback to catalog)
        let testList = [];
        try {
          const testApiUrl = `https://online-tech.in/api/Lab/GetTestAll?labId=${labId}&pageNumber=1&pageSize=1000`;
          const testRes = await axios.get(testApiUrl);
          const data = testRes?.data || [];
          testList = Array.isArray(data) ? data : (data?.data || []);
        } catch {
          const testRes = await getAllTests(1, 500);
          testList = Array.isArray(testRes?.data) ? testRes.data : (Array.isArray(testRes) ? testRes : []);
        }

        const normalizedTests = testList.map(t => {
          const sPrice = Number(t.SellingPrice ?? t.sellingPrice ?? t.price ?? 0);
          const tPrice = Number(t.TestPrice ?? t.testPrice ?? t.price ?? sPrice);
          const disc = Number(t.Discount ?? t.discount ?? 0);
          return {
            ...t,
            testID: t.TestID || t.testID || t.testId || t.id,
            testCode: t.TestCode || t.testCode || "",
            testName: t.TestName || t.testName || t.name || "Diagnostic Test",
            testPrice: tPrice > 0 ? tPrice : Math.round(sPrice * 1.4),
            sellingPrice: sPrice > 0 ? sPrice : tPrice,
            discount: disc,
            testImage: t.TestImage || t.testImage || "",
            labId: Number(t.LabId || t.labId || t.storeId || labId),
            labName: t.LabName || t.labName || labData?.name || stateLab?.name || "Certified Partner Lab",
            description: t.Description || t.description || "",
            reportTime: t.ReportTime || t.reportTime || 24,
            isActive: t.IsActive !== false && t.isActive !== false,
          };
        });

        // Filter valid active tests for this lab
        const seenTestIds = new Set();
        const validTests = [];
        for (const t of normalizedTests) {
          if (!t.testName || t.isActive === false) continue;
          if (seenTestIds.has(t.testID)) continue;
          seenTestIds.add(t.testID);
          validTests.push(t);
        }

        const thisLabTests = validTests.filter(t => Number(t.labId) === Number(labId));
        setTests(thisLabTests.length > 0 ? thisLabTests : validTests);

        // 4. Lab Packages - Load ALL packages for this lab
        const pkgRes = await axios.get(`https://admin.medrphalabs.com/api/Package?id=${labId || 0}`);
        let pkgList = [];
        if (Array.isArray(pkgRes.data)) {
          pkgList = pkgRes.data;
        } else if (Array.isArray(pkgRes.data?.data)) {
          pkgList = pkgRes.data.data;
        }

        const filteredPackages = pkgList
          .filter(p => p.isActive !== false && (p.packageName || p.name))
          .map(pkg => ({
            ...pkg,
            assignedLabName: labData?.name || stateLab?.name || pkg.labName || 'Certified Partner Lab'
          }));

        const thisLabPackages = filteredPackages.filter(p => Number(p.labId) === Number(labId));
        setPackages(thisLabPackages.length > 0 ? thisLabPackages : filteredPackages);

      } catch (err) {
        console.error("Error loading lab details:", err);
      } finally {
        setLoading(false);
      }
    };

    loadLabAndData();
  }, [labId, stateLab, coords]);

  const labName = lab?.lab_name || lab?.name || stateLab?.name || "DGchem Lab";
  const labImage = lab?.image || (lab?.lab_image ? `https://admin.medrphalabs.com/LabImage/${lab.lab_image}` : null);
  const labAddress = lab?.address || stateLab?.address || "Sector 6, Noida";

  const isTestInCart = (testId) => {
    return cart.some((item) => Number(item.productId) === Number(testId) && Number(item.categoryId) === 2);
  };

  const isPackageInCart = (packageId) => {
    return cart.some((item) => Number(item.productId) === Number(packageId) && Number(item.categoryId) === 3);
  };

  const handleTestCartToggle = async (test) => {
    const testId = test.testID || test.testId || test.id;
    if (isTestInCart(testId)) {
      removeItem(testId, 2);
    } else {
      await addWithReplaceCheck({
        productId: testId,
        name: test.testName || test.name,
        price: test.sellingPrice || test.testPrice || test.price || 0,
        discountedPrice: test.discount || 0,
        categoryId: 2,
        storeId: test.labId || labId || 1,
        image: test.testImage || '',
      });
    }
  };

  const handlePackageCartToggle = async (pkg) => {
    const pkgId = pkg.packageId || pkg.id;
    if (isPackageInCart(pkgId)) {
      removeItem(pkgId, 3);
    } else {
      await addWithReplaceCheck({
        productId: pkgId,
        name: pkg.packageName || pkg.name,
        price: pkg.packagePrice || pkg.price || 0,
        discountedPrice: pkg.discountPrice || 0,
        categoryId: 3,
        storeId: pkg.labId || labId || 1,
        image: pkg.image || '',
      });
    }
  };

  const toggleCompare = (testId) => {
    setSelectedForCompare((prev) => 
      prev.includes(testId) 
        ? prev.filter((id) => id !== testId) 
        : prev.length < 4 ? [...prev, testId] : prev
    );
  };

  // Helper for dynamic package image URL
  const getPackageImgUrl = (pkg) => {
    const raw = pkg.dynamicImageUrl || pkg.image || pkg.packageImage || pkg.PackageImage || pkg.package_image || pkg.packageImageUrl || pkg.imageUrl || pkg.Image || pkg.imagePath;
    if (raw && typeof raw === "string" && raw.trim()) {
      const clean = raw.trim();
      if (clean.startsWith("http")) return clean;
      const normalized = clean.replace(/^\/+/, '');
      if (normalized.startsWith("PackageImage/") || normalized.startsWith("LabImage/") || normalized.startsWith("TestImage/")) {
        return `https://admin.medrphalabs.com/${normalized}`;
      }
      return `https://admin.medrphalabs.com/PackageImage/${normalized}`;
    }
    return null;
  };

  const displayTests = tests.filter(t => 
    (t.testName || t.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayPackages = packages.filter(p => 
    (p.packageName || p.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalCartPrice = cart.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.qty) || 1), 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32 text-slate-800 font-sans">
      {LoginPopupComponent}

      {/* ================= 1. EXACT FLUTTER HEADER (Matches lab_test_list_page_new.dart _buildHeader) ================= */}
      <div className="bg-gradient-to-r from-[#E53935] to-[#C62828] text-white pt-5 pb-7 px-4 sm:px-6 lg:px-10 shadow-md rounded-b-[28px]">
        <div className="max-w-[1400px] mx-auto">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            
            <div className="flex items-center gap-3.5">
              {/* Flutter Back Button */}
              <button 
                onClick={() => navigate(-1)} 
                className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors text-white shrink-0 shadow-xs"
                title="Go Back"
              >
                <ArrowLeft size={18} />
              </button>

              {/* Lab Logo in 58x58 clean white container (Matches Flutter) */}
              <div className="w-[58px] h-[58px] rounded-[14px] bg-white shadow-md flex items-center justify-center shrink-0 p-1.5 overflow-hidden">
                {labImage ? (
                  <img 
                    src={labImage} 
                    alt={labName} 
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://cdn-icons-png.flaticon.com/128/10801/10801900.png";
                    }}
                  />
                ) : (
                  <Building2 size={26} className="text-[#E53935]" />
                )}
              </div>

              {/* Lab Info */}
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-white leading-tight truncate">
                  {labName}
                </h1>
                
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                    <CheckCircle2 size={10} />
                    <span>Verified</span>
                  </span>
                  <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                    100% NABL
                  </span>
                  {distance ? (
                    <span className="bg-yellow-400 text-slate-950 text-[10px] font-extrabold px-1.5 py-0.5 rounded">
                      {distance} km away
                    </span>
                  ) : (
                    <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      📍 Certified Center
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Search Bar inside Header */}
            <div className="w-full md:w-80">
              <div className="h-10 bg-white rounded-xl px-3.5 flex items-center justify-between shadow-inner text-slate-800">
                <div className="flex items-center gap-2 text-slate-400 w-full">
                  <Search size={16} className="text-[#E53935] shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search in ${labName}...`}
                    className="w-full bg-transparent focus:outline-none text-xs text-slate-800 placeholder-slate-400 font-medium"
                  />
                </div>
                {searchQuery ? (
                  <button onClick={() => setSearchQuery("")} className="text-slate-400 p-1 hover:text-slate-600">
                    <X size={14} />
                  </button>
                ) : (
                  <Mic size={16} className="text-[#E53935]" />
                )}
              </div>
            </div>

          </div>

        </div>
      </div>

      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 mt-5 space-y-6">
        
        {/* ================= 2. FLUTTER TAB CONTROLS (Diagnostic Tests vs Packages) ================= */}
        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2 bg-slate-200/80 p-1 rounded-xl max-w-md">
            <button
              onClick={() => setActiveTab("tests")}
              className={`px-5 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                activeTab === "tests"
                  ? "bg-white text-[#E53935] shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <FlaskConical size={14} />
              <span>Diagnostic Tests ({displayTests.length})</span>
            </button>
            <button
              onClick={() => setActiveTab("packages")}
              className={`px-5 py-2 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                activeTab === "packages"
                  ? "bg-white text-[#E53935] shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Package size={14} />
              <span>Health Packages ({displayPackages.length})</span>
            </button>
          </div>

          <div className="text-xs font-semibold text-slate-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Home Sample Collection Available</span>
          </div>
        </div>

        {/* ================= 3. TESTS CONTENT (Matches Flutter TestCardNew with Img & Fallback) ================= */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-4 border border-[#E8F0FE] animate-pulse flex items-center gap-3.5">
                <div className="w-[62px] h-[62px] bg-slate-200 rounded-xl shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                  <div className="h-3 bg-slate-100 rounded w-1/3" />
                  <div className="h-4 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === "tests" ? (
          displayTests.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 max-w-md mx-auto my-6">
              <FlaskConical size={44} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-800">No Diagnostic Tests Found</h3>
              <p className="text-xs text-slate-500 mt-1">No tests available for this lab matching "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 bg-[#E53935] text-white font-bold text-xs px-5 py-2.5 rounded-xl hover:bg-red-700 shadow-xs"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {displayTests.map((test, idx) => (
                <TestCard
                  key={test.testID || test.testId || idx}
                  test={test}
                  inCart={isTestInCart(test.testID || test.testId)}
                  onCartToggle={handleTestCartToggle}
                />
              ))}
            </div>
          )
        ) : (
          /* ================= 4. PACKAGES CONTENT (Reusable PackageCard) ================= */
          displayPackages.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-slate-200 max-w-md mx-auto my-6">
              <Package size={44} className="text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-800">No Health Packages Found</h3>
              <p className="text-xs text-slate-500 mt-1">No packages available for this lab matching "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-4 bg-[#E53935] text-white font-bold text-xs px-5 py-2.5 rounded-xl hover:bg-red-700 shadow-xs"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {displayPackages.map((pkg, idx) => (
                <PackageCard
                  key={pkg.packageId || pkg.id || idx}
                  pkg={pkg}
                  inCart={isPackageInCart(pkg.packageId || pkg.id)}
                  onCartToggle={handlePackageCartToggle}
                />
              ))}
            </div>
          )
        )}

      </div>

      {/* ================= 5. FLUTTER COMPARE FLOATING BAR ================= */}
      {selectedForCompare.length >= 2 && (
        <div className="fixed bottom-20 left-4 right-4 max-w-md mx-auto z-40 bg-[#1E1B4B] text-white p-3.5 rounded-2xl shadow-2xl flex items-center justify-between gap-3 border border-indigo-500/30">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2 overflow-hidden">
              {selectedForCompare.map((id, i) => (
                <div key={id} className="w-7 h-7 rounded-full bg-indigo-500 border-2 border-[#1E1B4B] flex items-center justify-center text-[10px] font-bold">
                  {i + 1}
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-bold text-white leading-tight">
                {selectedForCompare.length} Tests Selected
              </p>
              <p className="text-[10px] text-indigo-200">Ready to compare</p>
            </div>
          </div>

          <button
            onClick={() => navigate('/test-comparison', { state: { testIds: selectedForCompare } })}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-bold text-xs px-4 py-2 rounded-xl shadow-md flex items-center gap-1"
          >
            <span>Compare Now</span>
            <ChevronRight size={13} />
          </button>
        </div>
      )}

      {/* ================= 6. STICKY BOTTOM CART BAR ================= */}
      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 sm:p-4 shadow-2xl">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 text-[#E53935] flex items-center justify-center font-black">
                <ShoppingBag size={20} />
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500">
                  {cart.length} {cart.length === 1 ? "Item" : "Items"} in Cart
                </span>
                <p className="text-base sm:text-lg font-black text-slate-900">
                  Total: ₹{totalCartPrice}
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate("/cart")}
              className="bg-[#E53935] hover:bg-red-700 text-white font-black text-xs sm:text-sm px-6 py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5"
            >
              <span>View Cart & Checkout</span>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
